import { useState } from "react";
import Pillar3Image1 from "../../assets/Pillars/P3_sports1.jpg";
import Pillar3Image2 from "../../assets/Pillars/P1_endurance3.jpg";
import Pillar3Image3 from "../../assets/Pillars/P2_strength2.jpg";

export default function SportsPillar() {
  const images = [Pillar3Image1, Pillar3Image2, Pillar3Image3];
  const [frontIndex, setFrontIndex] = useState(0);

  const handleClick = (idx) => setFrontIndex(idx);

  return (
    <section
      id="sports"
      className="relative w-full py-20 px-6 md:px-12 bg-gradient-to-b from-black/20 to-black/10 overflow-hidden"
    >
      {/* Decorative large number */}
      <div className="absolute -left-8 -top-10 text-[12rem] font-bold text-white/5 select-none pointer-events-none">
        3
      </div>

      {/* Section Heading */}
      <div className="relative max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12 mb-16">
        <div className="md:w-1/2 space-y-6 text-center md:text-left">
          <h2 className="text-white text-4xl sm:text-5xl md:text-8xl font-extrabold drop-shadow-lg">
            Pillar 3: Sports Performance
          </h2>
          <p className="text-neonTurquoise text-lg sm:text-xl md:text-3xl font-semibold drop-shadow-md">
            Train Like an Athlete—For the Sport You Love.
          </p>
          <p className="text-gray-300 text-base sm:text-lg md:text-2xl max-w-xl drop-shadow-sm">
            Each sport demands unique combinations of strength, speed, balance,
            and mobility. Sport-specific training enhances performance while
            reducing injury risk and improving overall athletic capability.
          </p>
        </div>

        {/* Interactive Image Cluster */}
        <div className="md:w-1/2 relative flex justify-center items-center">
          <div className="relative w-full h-[28rem] md:h-[32rem]">
            {images.map((img, idx) => {
              const offset = idx - frontIndex;
              const zIndex = idx === frontIndex ? 20 : 10 - Math.abs(offset);
              const rotate = idx === frontIndex ? 0 : offset * 5; // rotate side images
              const left = 50 + offset * 6; // horizontal offset
              const top = Math.abs(offset) * 2; // slight vertical offset

              return (
                <img
                  key={idx}
                  src={img}
                  alt={`Endurance ${idx + 1}`}
                  onClick={() => handleClick(idx)}
                  className={`absolute w-4/5 h-full object-cover rounded-3xl shadow-2xl cursor-pointer transform transition-all duration-500`}
                  style={{
                    zIndex,
                    transform: `translate(-50%, ${top}%) rotate(${rotate}deg)`,
                    left: `${left}%`,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Training & Outcomes */}
      <div className="relative max-w-7xl mx-auto flex flex-col md:flex-row items-start gap-8">
        {/* Training Methods */}
        <div className="relative md:w-1/2 bg-gradient-to-br from-cyan-500/20 to-black/30 backdrop-blur-lg rounded-2xl p-10 shadow-2xl hover:shadow-3xl transition duration-500 group overflow-hidden flex flex-col">
          <h3 className="text-white text-3xl font-bold mb-6">
            Training Methods
          </h3>
          <div className="flex-1 overflow-y-auto">
            <ul className="text-gray-200 text-xl space-y-3 pr-2">
              {[
                "Soccer: Explosive legs, speed work, agility drills, and tactical awareness training for full-field performance enhancement.",
                "Tennis: Rotational power, footwork precision, shoulder stability, and endurance exercises tailored to tennis-specific movements and match demands.",
                "Golf: Hip mobility, core strength, rotational control, and swing mechanics to optimize distance, accuracy, and consistency on the course.",
                "Horse Riding: Postural symmetry, core endurance, hip stability, and balance exercises to improve riding efficiency, control, and performance.",
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 relative group-hover:text-gray-300 transition duration-500"
                >
                  <span className="text-neonTurquoise font-bold text-3xl mt-0">
                    •
                  </span>{" "}
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="absolute -top-6 -right-6 w-16 h-16 border-2 border-cyan-400 rounded-full opacity-20 animate-pulse-slow"></div>
        </div>

        {/* Connector arrow/line */}
        <div className="hidden md:flex flex-col justify-center items-center">
          <div className="w-12 h-1 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full animate-pulse-slow"></div>
          <div className="w-4 h-4 mt-2 bg-neonTurquoise rounded-full animate-bounce"></div>
        </div>

        {/* Outcomes */}
        <div className="relative md:w-1/2 bg-gradient-to-br from-cyan-500/20 to-black/30 backdrop-blur-lg rounded-2xl p-10 shadow-2xl hover:shadow-3xl transition duration-500 group overflow-hidden flex flex-col">
          <h3 className="text-white text-3xl font-bold mb-6">Your Outcomes</h3>
          <div className="flex-1 overflow-y-auto">
            <ul className="text-gray-200 text-xl space-y-4 pr-2">
              {[
                "Movement pattern correction, SAQ training, balance and reaction drills to optimize performance in your sport. These methods support joint control, improve force production, elevate speed accuracy, and create stable mechanics under high-intensity demands.",

                "Sport-specific mobility and strength conditioning tailored to the demands and mechanics of your chosen sport. The work targets joint range, controlled force output, tissue integrity, and precise movement quality required for consistent high-level performance.",
              ].map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 relative group-hover:text-gray-300 transition duration-500"
                >
                  <span className="text-neonTurquoise font-bold text-3xl mt-0">
                    •
                  </span>{" "}
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="absolute -bottom-6 -left-6 w-16 h-16 border-2 border-purple-400 rounded-full opacity-20 animate-pulse-slow"></div>
        </div>
      </div>
    </section>
  );
}
